import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { FileTextIcon, MapPinIcon, PackageIcon, RouteIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { LocationDTO } from '@invenco/accounts-interface';
import { ReturnCategory, ReturnStatus } from '@invenco/supply-interface';

import { Badge } from '@/components/core/badge';
import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  DataTableViewMenu,
  useDataTable,
  useDataTableState,
  useDataTableViews,
  useRowNavigationCallback,
  type View,
} from '@/components/data-table';
import { Header, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { StatusBadge } from '@/components/status-badge';
import { LIVE_DATA_INTERVAL_MS } from '@/constants';
import { useGateways } from '@/gateways/GatewayProvider';
import { QueryReturnsProps, ReturnSortField } from '@/gateways/impl/SupplyGateway';
import { formatDate } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';
import { returnStatusType } from '@/shared/status-maps';
import { returnCategoryTitle, returnStatusTitle } from '@/shared/title-maps';

import { ReturnListItem, ReturnListMapper } from './returns-list-mapper';

const columnHelper = createColumnHelper<ReturnListItem>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: ({ getValue }) => <Badge variant="secondary">{getValue()}</Badge>,
  }),
  columnHelper.accessor('orderName', {
    header: 'Order',
    meta: { truncate: true },
  }),
  columnHelper.accessor('customerName', {
    header: 'Customer',
    meta: { truncate: true },
  }),
  columnHelper.accessor('category', {
    header: 'Return type',
    cell: ({ getValue }) => {
      const category = getValue();
      return category ? returnCategoryTitle[category] : undefined;
    },
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue();
      return status ? (
        <StatusBadge status={status} type={returnStatusType} title={returnStatusTitle} />
      ) : undefined;
    },
    enableSorting: false,
  }),
  columnHelper.accessor('createdAt', {
    header: 'Date created',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? formatDate(value) : undefined;
    },
  }),
  columnHelper.accessor('locationName', {
    header: 'Location',
  }),
  columnHelper.accessor('closedAt', {
    header: 'Date closed',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? formatDate(value) : '-';
    },
  }),
];

// Maps a column accessor id to the GraphQL sort field it should sort on. The
// Customer/Order/Location columns sort on flattened or related fields.
const COLUMN_SORT_FIELDS: Record<string, ReturnSortField> = {
  name: 'name',
  orderName: 'orderName',
  customerName: 'fromAddressContactName',
  category: 'returnCategory',
  createdAt: 'createdAt',
  locationName: 'locationName',
  closedAt: 'closedAt',
};

const tieBreakerSort = [
  {
    key: 'createdAt',
    direction: 'desc',
  },
  {
    key: 'name',
    direction: 'asc',
  },
] as const;

const presetViews: View[] = [
  {
    id: 'all',
    name: 'All',
    state: {
      filters: [],
    },
  },
  ...[ReturnStatus.PENDING, ReturnStatus.OPEN, ReturnStatus.CLOSED, ReturnStatus.CANCELLED].map(
    (status) => ({
      id: status,
      name: returnStatusTitle[status],
      state: {
        filters: [
          {
            id: 'status',
            operator: 'in',
            value: [status],
          },
        ],
      },
    }),
  ),
];

export function ReturnsList() {
  const { supplyGateway, accountsGateway } = useGateways();
  const [isLive, setIsLive] = useState(false);
  const onClickRow = useRowNavigationCallback<ReturnListItem>('/supply/returns');

  const { views, isLoadingSavedViews, ...viewTabsProps } = useDataTableViews({
    tableId: 'returns',
    presetViews,
  });

  const { data: locations, isLoading: isLoadingLocations } = useQuery({
    queryKey: ['locations'],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      accountsGateway.getLocations(undefined, { signal }),
    select: ({ items }: { items: LocationDTO[] }) => items,
  });

  const locationsById = useMemo(
    () => new Map((locations ?? []).map((location) => [location.id, location])),
    [locations],
  );

  const { state, currentView, onChangeViewId, filters, ...tableProps } = useDataTableState({
    columns,
    filterDefs: {
      status: {
        label: 'Status',
        variant: 'multiselect',
        icon: FileTextIcon,
        options: [
          ReturnStatus.PENDING,
          ReturnStatus.OPEN,
          ReturnStatus.CLOSED,
          ReturnStatus.CANCELLED,
        ],
        optionLabel: (option) => returnStatusTitle[option as ReturnStatus] || option,
      },
      category: {
        label: 'Category',
        variant: 'multiselect',
        icon: PackageIcon,
        options: [
          ReturnCategory.CUSTOMER_RETURN,
          ReturnCategory.RETURN_TO_SENDER,
          ReturnCategory.REPLY_PAID,
          ReturnCategory.CANCELLED,
          ReturnCategory.UNKNOWN,
        ],
        optionLabel: (option) => returnCategoryTitle[option as ReturnCategory] || option,
      },
      location: {
        label: 'Location',
        variant: 'multiselect',
        icon: MapPinIcon,
        options: locations?.map((location) => location.id) ?? [],
        optionLabel: (option) => locationsById.get(option)?.name ?? option,
        isLoading: isLoadingLocations,
      },
    },
    views,
    isLoadingSavedViews,
  });

  const filterMap = filters.getActiveFilterMap();

  const queryInput: QueryReturnsProps = {
    take: state.pagination.pageSize,
    offset: state.pagination.pageSize * state.pagination.pageIndex,
    sortBy: [
      ...state.sorting.flatMap((s) => {
        const key = COLUMN_SORT_FIELDS[s.id];
        return key ? [{ key, direction: s.desc ? ('desc' as const) : ('asc' as const) }] : [];
      }),
      ...tieBreakerSort,
    ],
    filters: {
      status: filterMap.status,
      category: filterMap.category,
      location: filterMap.location,
    },
    search: filters.search,
  };

  const { data, isLoading, isFetching, isPlaceholderData, isError, refetch } = useQueryWithInput({
    parentKey: 'returns',
    input: queryInput,
    query: (input, { signal }) => supplyGateway.queryReturns(input, { signal }),
    select: ReturnListMapper.toData,
    refetchInterval: isLive ? LIVE_DATA_INTERVAL_MS : undefined,
    placeholderData: keepPreviousData,
  });

  const table = useDataTable({
    data: data?.data,
    rowCount: data?.total,
    columns,
    isLoading,
    isLoadingWithStaleData: isFetching && isPlaceholderData,
    hasError: isError,
    state,
    ...tableProps,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <RouteIcon />
        </HeaderIcon>
        <HeaderTitle>Returns</HeaderTitle>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableViewMenu
            views={views}
            currentView={currentView}
            filterState={filters.filterState}
            rowCount={data?.total}
            isLoadingRowCount={isLoading || isPlaceholderData}
            onChangeViewId={onChangeViewId}
            {...viewTabsProps}
          />
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetch} isLive={isLive} onChangeLive={setIsLive} />
        </DataTableToolbar>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </Page>
  );
}
